/**
 * SFHAEditor - System Functional Hazard Assessment Editor
 * 
 * Structured editor for creating and editing SFHA documents per ARP4761A.
 * Features:
 * - System function definition with hierarchy
 * - Failure condition identification
 * - Flight phase effects analysis
 * - Severity classification with probability objectives
 * - Derived safety requirements generation
 * 
 * COMPLIANCE: All classifications must trace to ARP4761A criteria.
 */

import { useState } from 'react';
import { Button, Input, Label, Textarea, Select } from '@/components/ui';
import type {
  SFHADocument,
  SystemFunction,
  FailureCondition,
  FlightPhaseEffect,
  SeverityClassification,
  FlightPhase,
  SFHAAssumption,
  DerivedSafetyRequirement,
} from '@aerotage/shared/safety';

// Probability objectives per severity (per flight hour)
const PROBABILITY_OBJECTIVES: Record<SeverityClassification, number> = {
  'Catastrophic': 1.0e-9,
  'Hazardous': 1.0e-7,
  'Major': 1.0e-5,
  'Minor': 1.0e-3,
  'No_Safety_Effect': Infinity,
};

const SEVERITY_OPTIONS: SeverityClassification[] = [
  'Catastrophic',
  'Hazardous',
  'Major',
  'Minor',
  'No_Safety_Effect',
];

const FLIGHT_PHASE_OPTIONS: FlightPhase[] = [
  'ground',
  'taxi',
  'takeoff',
  'climb',
  'cruise',
  'descent',
  'approach',
  'landing',
  'go_around',
];

interface SFHAEditorProps {
  document?: SFHADocument;
  projectId: string;
  readOnly?: boolean;
  onSave: (document: SFHADocument) => Promise<void>;
  onCancel: () => void;
}

type ActiveTab = 'functions' | 'failure-conditions' | 'severity-matrix' | 'requirements' | 'assumptions';

export function SFHAEditor({
  document,
  projectId: _projectId,
  readOnly = false,
  onSave,
  onCancel,
}: SFHAEditorProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('functions');
  const [functions, setFunctions] = useState<SystemFunction[]>(document?.safetyData?.functions || []);
  const [failureConditions, setFailureConditions] = useState<FailureCondition[]>(
    document?.safetyData?.failureConditions || []
  );
  const [assumptions, _setAssumptions] = useState<SFHAAssumption[]>(
    document?.safetyData?.assumptions || []
  );
  const [derivedRequirements, _setDerivedRequirements] = useState<DerivedSafetyRequirement[]>(
    document?.safetyData?.derivedSafetyRequirements || []
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [selectedFCId, setSelectedFCId] = useState<string | null>(null);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updatedDoc: SFHADocument = {
        ...document!,
        safetyData: {
          functions,
          failureConditions,
          assumptions,
          derivedSafetyRequirements: derivedRequirements,
        },
      };
      await onSave(updatedDoc);
    } catch (err) {
      setError(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Generate next ID
  const generateId = (prefix: string, items: { id: string }[]) => {
    const maxNum = items.reduce((max, item) => {
      const match = item.id.match(new RegExp(`^${prefix}-(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        return num > max ? num : max;
      }
      return max;
    }, 0);
    return `${prefix}-${String(maxNum + 1).padStart(3, '0')}`;
  };

  // Add new function
  const addFunction = () => {
    const newFunc: SystemFunction = {
      id: generateId('F', functions),
      name: '',
      description: '',
      functionType: 'intended_action',
      childFunctionIds: [],
      allocatedSystem: '',
      externalInterfaces: [],
      implicitFunction: false,
    };
    setFunctions([...functions, newFunc]);
    setSelectedFunctionId(newFunc.id);
  };

  // Update function
  const updateFunction = (id: string, updates: Partial<SystemFunction>) => {
    setFunctions(functions.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  // Delete function
  const deleteFunction = (id: string) => {
    setFunctions(functions.filter(f => f.id !== id));
    // Also update any child references
    setFunctions(prev => prev.map(f => ({
      ...f,
      childFunctionIds: f.childFunctionIds.filter(cid => cid !== id),
      parentFunctionId: f.parentFunctionId === id ? undefined : f.parentFunctionId,
    })));
    if (selectedFunctionId === id) setSelectedFunctionId(null);
  };

  // Add new failure condition
  const addFailureCondition = (functionId?: string) => {
    const newFC: FailureCondition = {
      id: generateId('FC', failureConditions),
      functionId: functionId || functions[0]?.id || '',
      failureType: 'total_loss',
      description: '',
      flightPhaseEffects: [],
      worstCaseSeverity: 'Minor',
      worstCasePhase: 'cruise',
      probabilityObjective: PROBABILITY_OBJECTIVES['Minor'],
      probabilityObjectiveBasis: 'ARP4761A Table 1',
      assumptions: [],
      mitigationStrategies: [],
    };
    setFailureConditions([...failureConditions, newFC]);
    setSelectedFCId(newFC.id);
  };

  // Update failure condition
  const updateFailureCondition = (id: string, updates: Partial<FailureCondition>) => {
    setFailureConditions(failureConditions.map(fc => {
      if (fc.id !== id) return fc;
      const updated = { ...fc, ...updates };
      // Auto-update probability objective when severity changes
      if (updates.worstCaseSeverity && updates.worstCaseSeverity !== fc.worstCaseSeverity) {
        updated.probabilityObjective = PROBABILITY_OBJECTIVES[updates.worstCaseSeverity];
      }
      return updated;
    }));
  };

  // Delete failure condition
  const deleteFailureCondition = (id: string) => {
    setFailureConditions(failureConditions.filter(fc => fc.id !== id));
    if (selectedFCId === id) setSelectedFCId(null);
  };

  // Add flight phase effect
  const addFlightPhaseEffect = (fcId: string, phase: FlightPhase) => {
    const newEffect: FlightPhaseEffect = {
      flightPhase: phase,
      effectOnAircraft: '',
      effectOnCrew: '',
      effectOnOccupants: '',
      crewAwareness: 'immediate',
      crewResponseTime: undefined,
      crewWorkload: 'low',
      severityClassification: 'Minor',
      severityRationale: '',
    };
    updateFailureCondition(fcId, {
      flightPhaseEffects: [
        ...(failureConditions.find(fc => fc.id === fcId)?.flightPhaseEffects || []),
        newEffect,
      ],
    });
  };

  // Get function by ID
  const getFunctionById = (id: string) => functions.find(f => f.id === id);

  // Get failure conditions for a function
  const _getFailureConditionsForFunction = (functionId: string) => 
    failureConditions.filter(fc => fc.functionId === functionId);

  const _selectedFunction = selectedFunctionId ? getFunctionById(selectedFunctionId) : null;
  const _selectedFC = selectedFCId ? failureConditions.find(fc => fc.id === selectedFCId) : null;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            SFHA Editor
          </h2>
          <p className="text-sm text-muted-foreground">
            System Functional Hazard Assessment
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={readOnly || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-destructive-light border border-destructive rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex-shrink-0 px-4 border-b border-border">
        <div className="flex gap-4">
          {(['functions', 'failure-conditions', 'severity-matrix', 'requirements', 'assumptions'] as ActiveTab[]).map(tab => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              label={tab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {activeTab === 'functions' && (
          <FunctionsPanel
            functions={functions}
            selectedId={selectedFunctionId}
            onSelect={setSelectedFunctionId}
            onAdd={addFunction}
            onUpdate={updateFunction}
            onDelete={deleteFunction}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'failure-conditions' && (
          <FailureConditionsPanel
            failureConditions={failureConditions}
            functions={functions}
            selectedId={selectedFCId}
            onSelect={setSelectedFCId}
            onAdd={addFailureCondition}
            onUpdate={updateFailureCondition}
            onDelete={deleteFailureCondition}
            onAddPhaseEffect={addFlightPhaseEffect}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'severity-matrix' && (
          <SeverityMatrixPanel
            failureConditions={failureConditions}
            functions={functions}
          />
        )}
        {activeTab === 'requirements' && (
          <RequirementsPanel
            requirements={derivedRequirements}
            failureConditions={failureConditions}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'assumptions' && (
          <AssumptionsPanel
            assumptions={assumptions}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

// Tab button component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function TabButton({ active, onClick, label }: TabButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`py-2 px-3 text-sm font-medium border-b-2 rounded-none h-auto ${
        active
          ? 'border-primary text-info'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </Button>
  );
}

// Functions panel
interface FunctionsPanelProps {
  functions: SystemFunction[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<SystemFunction>) => void;
  onDelete: (id: string) => void;
  readOnly: boolean;
}

function FunctionsPanel({
  functions,
  selectedId,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  readOnly,
}: FunctionsPanelProps) {
  const selectedFunction = selectedId ? functions.find(f => f.id === selectedId) : null;

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Function list */}
      <div className="w-1/3 border-r border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">System Functions</h3>
            {!readOnly && (
              <Button size="sm" onClick={onAdd}>+ Add</Button>
            )}
          </div>
          
          {functions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No functions defined yet</p>
              <p className="text-sm">Click "Add" to create a system function</p>
            </div>
          ) : (
            <div className="space-y-2">
              {functions.map(func => (
                <div
                  key={func.id}
                  onClick={() => onSelect(func.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedId === func.id
                      ? 'border-primary bg-info-light'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">{func.id}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {func.functionType.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="font-medium text-foreground mt-1">
                    {func.name || '(Untitled)'}
                  </div>
                  {func.allocatedSystem && (
                    <div className="text-xs text-muted-foreground mt-1">
                      → {func.allocatedSystem}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Function detail */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedFunction ? (
          <FunctionDetailForm
            function={selectedFunction}
            functions={functions}
            onUpdate={(updates) => onUpdate(selectedFunction.id, updates)}
            onDelete={() => onDelete(selectedFunction.id)}
            readOnly={readOnly}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Select a function to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Function detail form
interface FunctionDetailFormProps {
  function: SystemFunction;
  functions: SystemFunction[];
  onUpdate: (updates: Partial<SystemFunction>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function FunctionDetailForm({
  function: func,
  functions,
  onUpdate,
  onDelete,
  readOnly,
}: FunctionDetailFormProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{func.id}</h3>
        {!readOnly && (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>

      <div>
        <Label>Function Name *</Label>
        <Input
          value={func.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="e.g., Provide aircraft attitude information"
          disabled={readOnly}
        />
      </div>

      <div>
        <Label>Description *</Label>
        <Textarea
          value={func.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Detailed description of the function..."
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Function Type</Label>
          <Select
            value={func.functionType}
            onChange={(e) => onUpdate({ functionType: e.target.value as SystemFunction['functionType'] })}
            disabled={readOnly}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="intended_action">Intended Action</option>
            <option value="prevention">Prevention</option>
            <option value="provision_of_output">Provision of Output</option>
          </Select>
        </div>

        <div>
          <Label>Allocated System</Label>
          <Input
            value={func.allocatedSystem}
            onChange={(e) => onUpdate({ allocatedSystem: e.target.value })}
            placeholder="e.g., AHRS, FMS, Display System"
            disabled={readOnly}
          />
        </div>
      </div>

      <div>
        <Label>Parent Function (if sub-function)</Label>
        <Select
          value={func.parentFunctionId || ''}
          onChange={(e) => onUpdate({ parentFunctionId: e.target.value || undefined })}
          disabled={readOnly}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">(None - Top Level)</option>
          {functions.filter(f => f.id !== func.id).map(f => (
            <option key={f.id} value={f.id}>{f.id}: {f.name}</option>
          ))}
        </Select>
      </div>

      <div>
        <Label className="flex items-center gap-2">
          {/* Checkbox: no Checkbox UI component available */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <input
            type="checkbox"
            checked={func.implicitFunction}
            onChange={(e) => onUpdate({ implicitFunction: e.target.checked })}
            disabled={readOnly}
          />
          Implicit Function (not explicitly stated in requirements)
        </Label>
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea
          value={func.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}

// Failure Conditions Panel
interface FailureConditionsPanelProps {
  failureConditions: FailureCondition[];
  functions: SystemFunction[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAdd: (functionId?: string) => void;
  onUpdate: (id: string, updates: Partial<FailureCondition>) => void;
  onDelete: (id: string) => void;
  onAddPhaseEffect: (fcId: string, phase: FlightPhase) => void;
  readOnly: boolean;
}

function FailureConditionsPanel({
  failureConditions,
  functions,
  selectedId,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  onAddPhaseEffect,
  readOnly,
}: FailureConditionsPanelProps) {
  const selectedFC = selectedId ? failureConditions.find(fc => fc.id === selectedId) : null;

  const getSeverityColor = (severity: SeverityClassification) => {
    // Uses semantic severity tokens - auto-switches for dark mode
    switch (severity) {
      case 'Catastrophic': return 'bg-severity-catastrophic-bg text-severity-catastrophic-text';
      case 'Hazardous': return 'bg-severity-hazardous-bg text-severity-hazardous-text';
      case 'Major': return 'bg-severity-major-bg text-severity-major-text';
      case 'Minor': return 'bg-severity-minor-bg text-severity-minor-text';
      default: return 'bg-severity-none-bg text-severity-none-text';
    }
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* FC list */}
      <div className="w-1/3 border-r border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Failure Conditions</h3>
            {!readOnly && (
              <Button size="sm" onClick={() => onAdd()}>+ Add</Button>
            )}
          </div>
          
          {failureConditions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No failure conditions defined</p>
              <p className="text-sm">Define system functions first, then identify failure conditions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {failureConditions.map(fc => {
                const func = functions.find(f => f.id === fc.functionId);
                return (
                  <div
                    key={fc.id}
                    onClick={() => onSelect(fc.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedId === fc.id
                        ? 'border-primary bg-info-light'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{fc.id}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityColor(fc.worstCaseSeverity)}`}>
                        {fc.worstCaseSeverity}
                      </span>
                    </div>
                    <div className="text-sm text-foreground mt-1 line-clamp-2">
                      {fc.description || '(No description)'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Function: {func?.name || fc.functionId}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* FC detail */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedFC ? (
          <FailureConditionDetailForm
            fc={selectedFC}
            functions={functions}
            onUpdate={(updates) => onUpdate(selectedFC.id, updates)}
            onDelete={() => onDelete(selectedFC.id)}
            onAddPhaseEffect={(phase) => onAddPhaseEffect(selectedFC.id, phase)}
            readOnly={readOnly}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Select a failure condition to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Failure Condition detail form
interface FailureConditionDetailFormProps {
  fc: FailureCondition;
  functions: SystemFunction[];
  onUpdate: (updates: Partial<FailureCondition>) => void;
  onDelete: () => void;
  onAddPhaseEffect: (phase: FlightPhase) => void;
  readOnly: boolean;
}

function FailureConditionDetailForm({
  fc,
  functions,
  onUpdate,
  onDelete,
  onAddPhaseEffect,
  readOnly,
}: FailureConditionDetailFormProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{fc.id}</h3>
        {!readOnly && (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Related Function *</Label>
          <Select
            value={fc.functionId}
            onChange={(e) => onUpdate({ functionId: e.target.value })}
            disabled={readOnly}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {functions.map(f => (
              <option key={f.id} value={f.id}>{f.id}: {f.name}</option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Failure Type</Label>
          <Select
            value={fc.failureType}
            onChange={(e) => onUpdate({ failureType: e.target.value as FailureCondition['failureType'] })}
            disabled={readOnly}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="total_loss">Total Loss of Function</option>
            <option value="partial_loss">Partial Loss of Function</option>
            <option value="malfunction">Malfunction</option>
          </Select>
        </div>
      </div>

      <div>
        <Label>Failure Condition Description *</Label>
        <Textarea
          value={fc.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="e.g., Loss of all attitude information displayed to the crew"
          rows={2}
          disabled={readOnly}
        />
      </div>

      <div>
        <Label>Specific Failure Mode</Label>
        <Input
          value={fc.failureMode || ''}
          onChange={(e) => onUpdate({ failureMode: e.target.value })}
          placeholder="e.g., Erroneous display, Stuck-at, Intermittent"
          disabled={readOnly}
        />
      </div>

      {/* Severity Classification */}
      <div className="p-4 border border-border rounded-lg bg-muted/30">
        <h4 className="font-medium text-foreground mb-3">Severity Classification</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Worst Case Severity *</Label>
            <Select
              value={fc.worstCaseSeverity}
              onChange={(e) => onUpdate({ worstCaseSeverity: e.target.value as SeverityClassification })}
              disabled={readOnly}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {SEVERITY_OPTIONS.map(s => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </Select>
          </div>

          <div>
            <Label>Worst Case Phase</Label>
            <Select
              value={fc.worstCasePhase}
              onChange={(e) => onUpdate({ worstCasePhase: e.target.value as FlightPhase })}
              disabled={readOnly}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {FLIGHT_PHASE_OPTIONS.map(p => (
                <option key={p} value={p}>{p.replace('_', ' ')}</option>
              ))}
            </Select>
          </div>

          <div>
            <Label>Probability Objective</Label>
            <Input
              value={fc.probabilityObjective.toExponential(0)}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {fc.probabilityObjectiveBasis}
            </p>
          </div>
        </div>
      </div>

      {/* Flight Phase Effects */}
      <div className="p-4 border border-border rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-foreground">Flight Phase Effects</h4>
          {!readOnly && (
            <Select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  onAddPhaseEffect(e.target.value as FlightPhase);
                  e.target.value = '';
                }
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">+ Add Phase...</option>
              {FLIGHT_PHASE_OPTIONS.filter(p => 
                !fc.flightPhaseEffects.some(e => e.flightPhase === p)
              ).map(p => (
                <option key={p} value={p}>{p.replace('_', ' ')}</option>
              ))}
            </Select>
          )}
        </div>
        
        {fc.flightPhaseEffects.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No flight phase effects defined. Add phases to analyze effects during different phases of flight.
          </p>
        ) : (
          <div className="space-y-3">
            {fc.flightPhaseEffects.map((effect, idx) => (
              <div key={effect.flightPhase} className="p-3 bg-muted/30 rounded">
                <div className="font-medium text-sm text-foreground capitalize mb-2">
                  {effect.flightPhase.replace('_', ' ')}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <Label className="text-xs">Effect on Aircraft</Label>
                    <Input
                      value={effect.effectOnAircraft}
                      onChange={(e) => {
                        const newEffects = [...fc.flightPhaseEffects];
                        newEffects[idx] = { ...effect, effectOnAircraft: e.target.value };
                        onUpdate({ flightPhaseEffects: newEffects });
                      }}
                      disabled={readOnly}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Effect on Crew</Label>
                    <Input
                      value={effect.effectOnCrew}
                      onChange={(e) => {
                        const newEffects = [...fc.flightPhaseEffects];
                        newEffects[idx] = { ...effect, effectOnCrew: e.target.value };
                        onUpdate({ flightPhaseEffects: newEffects });
                      }}
                      disabled={readOnly}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mitigation */}
      <div>
        <Label>Mitigation Strategies</Label>
        <Textarea
          value={fc.mitigationStrategies.join('\n')}
          onChange={(e) => onUpdate({ mitigationStrategies: e.target.value.split('\n').filter(s => s.trim()) })}
          placeholder="Enter each mitigation strategy on a new line..."
          rows={3}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}

// Severity Matrix Panel
interface SeverityMatrixPanelProps {
  failureConditions: FailureCondition[];
  functions: SystemFunction[];
}

function SeverityMatrixPanel({ failureConditions, functions }: SeverityMatrixPanelProps) {
  const getSeverityColor = (severity: SeverityClassification) => {
    switch (severity) {
      case 'Catastrophic': return 'bg-red-500';
      case 'Hazardous': return 'bg-orange-500';
      case 'Major': return 'bg-yellow-500';
      case 'Minor': return 'bg-blue-500';
      default: return 'bg-green-500';
    }
  };

  return (
    <div className="flex-1 overflow-auto p-4">
      <h3 className="font-semibold text-foreground mb-4">Severity Classification Matrix</h3>
      
      {failureConditions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No failure conditions to display</p>
          <p className="text-sm">Define failure conditions first to see the severity matrix</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="p-2 text-left font-medium text-muted-foreground">FC ID</th>
                <th className="p-2 text-left font-medium text-muted-foreground">Description</th>
                <th className="p-2 text-left font-medium text-muted-foreground">Function</th>
                <th className="p-2 text-left font-medium text-muted-foreground">Severity</th>
                <th className="p-2 text-left font-medium text-muted-foreground">Phase</th>
                <th className="p-2 text-left font-medium text-muted-foreground">Probability</th>
              </tr>
            </thead>
            <tbody>
              {failureConditions.map(fc => {
                const func = functions.find(f => f.id === fc.functionId);
                return (
                  <tr key={fc.id} className="border-b border-border hover:bg-muted/30">
                    <td className="p-2 font-mono text-xs">{fc.id}</td>
                    <td className="p-2 max-w-xs truncate">{fc.description}</td>
                    <td className="p-2">{func?.name || fc.functionId}</td>
                    <td className="p-2">
                      <span className={`inline-flex items-center gap-1.5`}>
                        <span className={`w-2 h-2 rounded-full ${getSeverityColor(fc.worstCaseSeverity)}`}></span>
                        {fc.worstCaseSeverity.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-2 capitalize">{fc.worstCasePhase.replace('_', ' ')}</td>
                    <td className="p-2 font-mono text-xs">{fc.probabilityObjective.toExponential(0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Requirements Panel (placeholder)
interface RequirementsPanelProps {
  requirements: DerivedSafetyRequirement[];
  failureConditions: FailureCondition[];
  readOnly: boolean;
}

function RequirementsPanel({ requirements, failureConditions: _failureConditions, readOnly: _readOnly }: RequirementsPanelProps) {
  return (
    <div className="flex-1 overflow-auto p-4">
      <h3 className="font-semibold text-foreground mb-4">Derived Safety Requirements</h3>
      <p className="text-muted-foreground text-sm mb-4">
        Safety requirements derived from failure condition analysis. These flow down to design and verification.
      </p>
      
      {requirements.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No derived requirements yet</p>
          <p className="text-sm">Requirements will be derived from failure condition analysis</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requirements.map(req => (
            <div key={req.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs text-muted-foreground">{req.id}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {req.requirementType}
                </span>
              </div>
              <p className="text-foreground">{req.requirementText}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Rationale: {req.rationale}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Assumptions Panel (placeholder)
interface AssumptionsPanelProps {
  assumptions: SFHAAssumption[];
  readOnly: boolean;
}

function AssumptionsPanel({ assumptions, readOnly: _readOnly }: AssumptionsPanelProps) {
  return (
    <div className="flex-1 overflow-auto p-4">
      <h3 className="font-semibold text-foreground mb-4">Assumptions</h3>
      <p className="text-muted-foreground text-sm mb-4">
        Assumptions made during the hazard assessment. These must be validated.
      </p>
      
      {assumptions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No assumptions documented</p>
          <p className="text-sm">Document assumptions made during analysis for validation tracking</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assumptions.map(asmp => (
            <div key={asmp.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs text-muted-foreground">{asmp.id}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {asmp.assumptionType.replace(/_/g, ' ')}
                </span>
                {asmp.validationRequired && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-warning-light text-warning">
                    Requires Validation
                  </span>
                )}
              </div>
              <p className="text-foreground">{asmp.assumptionText}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SFHAEditor;
